import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from './usersContext';
import api from './utils/api';
import StatusMessage from './components/StatusMessage';
import { useStatusMessage } from './hooks/useStatusMessage';
import './Auth.css';

function StripeConnectOnboarding() {
  const navigate = useNavigate();
  const { userDoc, userStatus, signOutUser } = useUser();
  const statusMsg = useStatusMessage();
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [stripeUrl, setStripeUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Only tutors should access this page
    if (userStatus === 'verified' && userDoc?.userType === 'tutor') {
      checkOrCreateStripeAccount();
    } else if (userStatus === 'verified' && userDoc?.userType !== 'tutor') {
      // Non-tutors go directly to dashboard
      navigate('/dashboard');
    } else if (userStatus !== 'verified') {
      // Non-verified users go to auth
      navigate('/Auth');
    }
  }, [userStatus, userDoc]);

  const checkOrCreateStripeAccount = async () => {
    try {
      setIsCreatingAccount(true);
      setError(null);
      
      // First check if they already have a Connect account
      const statusResponse = await api.post('/api/checkStripeConnectStatus', {});
      const statusData = await statusResponse.json();
      
      if (statusData.hasAccount && statusData.accountStatus?.chargesEnabled && statusData.accountStatus?.payoutsEnabled) {
        // Account is already set up, go to dashboard
        statusMsg.showSuccess('Stripe account already configured!');
        setTimeout(() => navigate('/dashboard'), 1500);
        return;
      }
      
      // Create or get existing Connect account
      const createResponse = await api.post('/api/createStripeConnectAccount', {
        country: 'CA' // You could make this dynamic based on user location
      });
      
      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(errorData.message || 'Failed to create Stripe account');
      }
      
      // Get onboarding link
      const returnUrl = `${window.location.origin}/stripe-connect-return`;
      const refreshUrl = `${window.location.origin}/stripe-connect-onboarding`;
      
      const onboardingResponse = await api.post('/api/getStripeConnectOnboardingLink', {
        returnUrl,
        refreshUrl
      });
      
      if (!onboardingResponse.ok) {
        const errorData = await onboardingResponse.json();
        throw new Error(errorData.message || 'Failed to get onboarding link');
      }
      
      const onboardingData = await onboardingResponse.json();
      setStripeUrl(onboardingData.url);
      
      // Redirect to Stripe after a short delay
      setTimeout(() => {
        window.location.href = onboardingData.url;
      }, 2000);
      
    } catch (error) {
      console.error('Error setting up Stripe Connect:', error);
      setError(error.message || 'Failed to set up payment account');
      setIsCreatingAccount(false);
      statusMsg.showError(error.message || 'Failed to set up payment account');
    }
  };

  const handleRetry = () => {
    setError(null);
    checkOrCreateStripeAccount();
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form-container">
        <h2 className="auth-title">Complete Your Tutor Account Setup</h2>
        
        {isCreatingAccount && !stripeUrl && !error && (
          <div className="auth-message">
            <p>Creating your Stripe Connect account...</p>
            <div className="loading-spinner"></div>
          </div>
        )}
        
        {stripeUrl && (
          <div className="auth-message">
            <p>Redirecting you to Stripe to complete your account setup...</p>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
              This is required to receive payments for your tutoring sessions.
            </p>
            <div className="loading-spinner"></div>
          </div>
        )}
        
        {error && (
          <div className="auth-message" style={{ marginBottom: '2rem' }}>
            <p style={{ color: '#d32f2f', marginBottom: '1rem' }}>
              {error}
            </p>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1.5rem' }}>
              Payment account setup is required to work as a tutor on our platform.
            </p>
            <button
              onClick={handleRetry}
              className="auth-submit-button"
              style={{ marginBottom: '1rem' }}
            >
              Try Again
            </button>
          </div>
        )}
        
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
            Having trouble? Contact support at support@brightbridgetutors.ca
          </p>
          <button
            onClick={handleSignOut}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            Sign out and try again later
          </button>
        </div>
      </div>
      
      <StatusMessage
        message={statusMsg.message}
        type={statusMsg.type}
        isVisible={statusMsg.isVisible}
        onClose={statusMsg.hideMessage}
        position="top-center"
      />
    </div>
  );
}

export default StripeConnectOnboarding;